import { useMutation } from '@apollo/client';
import * as Dialog from '@radix-ui/react-dialog';
import { CirclePlus, Plus, User, Users, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SHARE } from '../../constants/share.constants';
import { SHARE_STORY } from '../../graphql/resolvers/shareMutations/shareStory.gql';
import { useCircles } from '../../hooks/useCircles';
import CreateCircleModal from '../Circles/CreateCircleModal';
import ButtonComponent from '../UI/Button/Button.component';
import './share-story.scss';

interface ShareStoryModalProps {
  open: boolean;
  onClose: () => void;
  storyId: string;
  storyTitle: string;
  storyUrl: string;
}

const MAX_USER_INPUTS = 5;

const ShareStoryModal = ({
  open,
  onClose,
  storyId,
  storyTitle,
  storyUrl,
}: ShareStoryModalProps) => {
  const [userInputs, setUserInputs] = useState<string[]>(['']);
  const [selectedCircleId, setSelectedCircleId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isCreateCircleOpen, setIsCreateCircleOpen] = useState(false);

  const { circles, loading: circlesLoading } = useCircles();

  const [shareStory, { loading }] = useMutation(SHARE_STORY);

  useEffect(() => {
    if (open) {
      setUserInputs(['']);
      setSelectedCircleId(null);
      setError(null);
      setSuccess(false);
    }
  }, [open]);

  const handleUserChange = (index: number, value: string) => {
    setUserInputs((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleRemoveInput = (index: number) => {
    if (userInputs.length <= 1) return;
    setUserInputs((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAddInput = () => {
    if (userInputs.length >= MAX_USER_INPUTS) return;
    setUserInputs((prev) => [...prev, '']);
  };

  const handleShare = async () => {
    setError(null);

    const cleanedUsers = userInputs
      .map((u) => u.trim().replace(/^@/, ''))
      .filter((u) => u.length > 0);

    if (cleanedUsers.length === 0 && !selectedCircleId) {
      setError(SHARE.ERRORS.NO_RECIPIENTS);
      return;
    }

    try {
      const input: Record<string, unknown> = {
        storyId,
        title: storyTitle,
        storyUrl,
      };
      if (cleanedUsers.length > 0) input.usernames = cleanedUsers;
      if (selectedCircleId) input.circleId = selectedCircleId;

      await shareStory({ variables: { input } });
      setSuccess(true);
      setTimeout(() => onClose(), 1500);
    } catch (err) {
      setError(err instanceof Error ? err.message : SHARE.ERRORS.FAILED);
    }
  };

  return (
    <>
      <Dialog.Root
        open={open && !isCreateCircleOpen}
        onOpenChange={(isOpen) => !isOpen && onClose()}
      >
        <Dialog.Portal>
          <Dialog.Overlay className="share-modal__overlay" />
          <Dialog.Content className="share-modal__content">
            <Dialog.Title className="share-modal__title">
              {SHARE.MODAL_TITLE}
            </Dialog.Title>

            <p className="share-modal__story-name">{storyTitle}</p>

            {success ? (
              <div className="share-modal__success">{SHARE.SUCCESS}</div>
            ) : (
              <>
                <div className="share-modal__section">
                  <div className="share-modal__section-header">
                    <User size={16} />
                    <span>{SHARE.SECTION_USERS}</span>
                  </div>
                  <div className="share-modal__user-inputs">
                    {userInputs.map((value, index) => (
                      <div key={index} className="share-modal__user-row">
                        <input
                          type="text"
                          value={value}
                          onChange={(e) =>
                            handleUserChange(index, e.target.value)
                          }
                          placeholder={SHARE.USERNAME_PLACEHOLDER}
                        />
                        {userInputs.length > 1 && (
                          <button
                            type="button"
                            onClick={() => handleRemoveInput(index)}
                            aria-label={SHARE.ARIA_REMOVE}
                          >
                            <X size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  {userInputs.length < MAX_USER_INPUTS && (
                    <button
                      type="button"
                      className="share-modal__add-btn"
                      onClick={handleAddInput}
                    >
                      <Plus size={14} />
                      {SHARE.ADD_USER}
                    </button>
                  )}
                </div>

                <div className="share-modal__section">
                  <div className="share-modal__section-header">
                    <Users size={16} />
                    <span>{SHARE.SECTION_CIRCLE}</span>
                  </div>

                  {circlesLoading ? (
                    <p className="share-modal__hint">{SHARE.LOADING_CIRCLES}</p>
                  ) : circles.length === 0 ? (
                    <div className="share-modal__no-circles">
                      <p>{SHARE.NO_CIRCLES}</p>
                      <button
                        type="button"
                        className="share-modal__create-circle-btn"
                        onClick={() => setIsCreateCircleOpen(true)}
                      >
                        <CirclePlus size={14} />
                        {SHARE.CREATE_CIRCLE}
                      </button>
                    </div>
                  ) : (
                    <div className="share-modal__circle-list">
                      {circles.map((circle) => (
                        <button
                          key={circle.id}
                          type="button"
                          className={`share-modal__circle-option ${selectedCircleId === circle.id ? 'share-modal__circle-option--selected' : ''}`}
                          onClick={() =>
                            setSelectedCircleId(
                              selectedCircleId === circle.id ? null : circle.id,
                            )
                          }
                        >
                          <span className="share-modal__circle-name">
                            {circle.name}
                          </span>
                          <span className="share-modal__circle-count">
                            {circle.memberCount}{' '}
                            {circle.memberCount === 1
                              ? SHARE.MEMBER
                              : SHARE.MEMBERS}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {error && <p className="share-modal__error">{error}</p>}

                <div className="share-modal__footer">
                  <ButtonComponent variant="secondary" onClick={onClose}>
                    {SHARE.BUTTONS.CANCEL}
                  </ButtonComponent>
                  <ButtonComponent
                    variant="primary"
                    onClick={handleShare}
                    loading={loading}
                    disabled={loading}
                  >
                    {SHARE.BUTTONS.SHARE}
                  </ButtonComponent>
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <CreateCircleModal
        open={isCreateCircleOpen}
        onClose={() => setIsCreateCircleOpen(false)}
      />
    </>
  );
};

export default ShareStoryModal;
